/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ICrossEntityInventoryNode, ICrossEntityStockLabel } from './cross-entity-inventory-types';

/**
 * Configuration consumed by the stock-label derivation. Thresholds normally flow from
 * `app.config.outOfStockThreshold` and `app.config.lowStockThreshold`.
 */
export interface ICrossEntityStockLabelConfig {
    enableStockCheck?: boolean;
    outOfStockThreshold?: number;
    lowStockThreshold?: number;
}

/**
 * Derives an In/Low/Out-of-stock label for a product-scoped node using two configurable thresholds.
 *
 * Rules (apply to both B2B and B2C):
 * - stock check disabled                  => no label.
 * - qty &lt;= outOfStockThreshold         => OUT OF STOCK (no number).
 * - qty &lt;= lowStockThreshold           => LOW STOCK (number shown).
 * - otherwise                             => IN STOCK (no number).
 *
 * Out-of-stock is always evaluated first so a misconfigured `lowStockThreshold &lt; outOfStockThreshold`
 * never hides an out-of-stock node.
 * @param node - The product-scoped node.
 * @param config - The threshold configuration.
 * @returns The derived stock label, or undefined when stock check is disabled.
 */
export const deriveCrossEntityStockLabel = (
    node: ICrossEntityInventoryNode | undefined,
    config: ICrossEntityStockLabelConfig
): ICrossEntityStockLabel | undefined => {
    if (!config.enableStockCheck) {
        return undefined;
    }

    const quantity = node?.availableQuantity ?? 0;
    const outOfStockThreshold = config.outOfStockThreshold ?? 0;
    const lowStockThreshold = config.lowStockThreshold;

    if (quantity <= outOfStockThreshold) {
        return { status: 'outOfStock' };
    }

    if (lowStockThreshold !== undefined && quantity <= lowStockThreshold) {
        return { status: 'lowStock', quantity };
    }

    return { status: 'inStock' };
};
